import { Injectable, Type } from '@angular/core';
import { NavigationEnd, PRIMARY_OUTLET, Router } from '@angular/router';
import { Ask } from '@app/models/ebo/digital-logs/asks/ask';
import { HourlyWorkTypeMapping } from '@app/models/ebo/digital-logs/hourly-work-type.enum';
import { EProcess } from '@app/models/ebo/process/e-process';
import { EProcessType } from '@app/models/ebo/process/e-process-type.enum';
import { DigitalLogType } from '@app/models/ebo/shared/digital-log-type.enum';
import { StatusSync } from '@app/models/ebo/shared/status-sync';
import { StatusSyncSchema } from '@app/models/ebo/shared/status-sync.schema';
import { UserExperience } from '@app/models/ebo/shared/user-experience';
import { TaskFunctions } from '@app/modules/process/shared/task-to-url';
import { DefaultIcon, SDate } from '@summit/core';
import { DialogEditConfig, SummitDialogDirective } from '@summit/form';
import { BannerNotice, SummitConfirmConfig } from '@summit/models';
import { SummitNotificationService } from '@summit/services';
import { PageLoadingReactivePrimeComponent } from '@summit/ui';
import { Dayjs } from 'dayjs';
import { DialogService, DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import {
  auditTime,
  BehaviorSubject,
  combineLatest,
  distinctUntilChanged,
  filter,
  map,
  Observable,
  Subscription,
  take,
  takeUntil,
  timer
} from 'rxjs';
import { CrudService } from '../crud/crud.service';
import { BackEndJob } from '../job-dispatcher/back-end-job';
import { UserExperienceService } from '../user-experience.service';

@Injectable({
  providedIn: 'root'
})
export class NotificationService extends SummitNotificationService {
  schema = new StatusSyncSchema();
  statusSyncBs: BehaviorSubject<StatusSync | undefined> = new BehaviorSubject<StatusSync | undefined>(undefined);
  statusSync$ = this.statusSyncBs.asObservable();
  sDialog = new SummitDialogDirective();
  overlayDialogRef: DynamicDialogRef;
  overlayDialogBs: BehaviorSubject<string> = new BehaviorSubject<string>('Loading...');

  // Memoization cache for statusSync derived arrays
  private lastStatusSyncRef: StatusSync | undefined;
  private memoCache = {
    backEndJobs: [] as BackEndJob[],
    askInProgress: [] as Ask[],
    inbox: [] as EProcess[]
  };
  private deriveStatusSyncArrays(statusSync: StatusSync | undefined) {
    if (statusSync === this.lastStatusSyncRef) return this.memoCache;
    this.lastStatusSyncRef = statusSync;
    this.memoCache.backEndJobs = statusSync?.backEndJobs ?? [];
    this.memoCache.askInProgress = statusSync?.askInProgress ?? [];
    this.memoCache.inbox = statusSync?.inbox ?? [];
    return this.memoCache;
  }

  jobsState$ = combineLatest([this.statusSync$, this.uxService.currentUserExperience]).pipe(
    // Coalesce rapid bursts within a frame (~16ms) to reduce churn on Safari
    auditTime(16),
    map(([statusSync, ux]) => {
      const { backEndJobs } = this.deriveStatusSyncArrays(statusSync);
      const employeeId = ux?.activeEmployeeID;
      // Compute once per emission; single-pass partition reduces Safari filter cost
      const nowMinusHour = SDate.newUtc().add(-1, 'hour');
      const myJobs: BackEndJob[] = [];
      const otherJobs: BackEndJob[] = [];
      const myPending: BackEndJob[] = [];
      const otherPending: BackEndJob[] = [];
      for (const j of backEndJobs) {
        const isMine = j.employee?.id === employeeId;
        if (isMine) {
          myJobs.push(j);
        } else {
          otherJobs.push(j);
        }
        if (j.isInProgress && j.lastUpdate?.isAfter(nowMinusHour)) {
          (isMine ? myPending : otherPending).push(j);
        }
      }
      return { myJobs, otherJobs, myPending, otherPending };
    }),
    distinctUntilChanged((a, b) => {
      // Fast length check bail-out
      if (
        a.myJobs.length !== b.myJobs.length ||
        a.otherJobs.length !== b.otherJobs.length ||
        a.myPending.length !== b.myPending.length ||
        a.otherPending.length !== b.otherPending.length
      ) {
        return false;
      }
      // Build lightweight signatures including id, progress flag and timestamp
      const buildSig = (arr: BackEndJob[]) =>
        arr.map((j) => `${(j as any).id}|${j.isInProgress ? 1 : 0}|${j.lastUpdate?.valueOf() ?? 0}`).join(';');
      return (
        buildSig(a.myJobs) === buildSig(b.myJobs) &&
        buildSig(a.otherJobs) === buildSig(b.otherJobs) &&
        buildSig(a.myPending) === buildSig(b.myPending) &&
        buildSig(a.otherPending) === buildSig(b.otherPending)
      );
    })
  );

  areMyJobsPending$ = this.jobsState$.pipe(map((s) => s.myPending.length > 0));
  areJobsPending$ = this.jobsState$.pipe(map((s) => s.myPending.length > 0 || s.otherPending.length > 0));
  backEndJobs$ = this.jobsState$.pipe(map((s) => s.myJobs.concat(s.otherJobs)));
  myJobs$ = this.jobsState$.pipe(map((s) => s.myJobs));
  myPendingJobs$ = this.jobsState$.pipe(map((s) => s.myPending));
  otherJobs$ = this.jobsState$.pipe(map((s) => s.otherJobs));
  otherPendingJobs$ = this.jobsState$.pipe(map((s) => s.otherPending));
  inbox$: Observable<EProcess[]> = this.statusSync$.pipe(
    map((statusSync) => this.deriveStatusSyncArrays(statusSync).inbox)
  );
  inboxCount$: Observable<number> = this.inbox$.pipe(map((inbox) => inbox.length));
  asksInProgress$: Observable<Ask[]> = this.statusSync$.pipe(
    map((statusSync) => this.deriveStatusSyncArrays(statusSync).askInProgress)
  );
  private bannerKey = (notice: BannerNotice | undefined) => (notice ? `${notice.icon}|${notice.description}` : 'none');
  bannerNotice$ = combineLatest([
    this.uxService.currentUserExperience,
    this.statusSync$,
    this.router.events.pipe(
      filter((event) => event instanceof NavigationEnd),
      map((event) => event as NavigationEnd)
    )
  ]).pipe(
    map(([ux, statusSync, navEnd]) => {
      const { inbox, askInProgress } = this.deriveStatusSyncArrays(statusSync);
      const onboarding = inbox.find((x) => x.type === EProcessType.Onboarding);
      if (onboarding) {
        const onboardingUrl = TaskFunctions.toUrl2(this.router, onboarding, ux);
        return new BannerNotice({
          buttonText: 'Go to Setup',
          routerLink: onboardingUrl?.root.children[PRIMARY_OUTLET].toString(),
          queryParams: onboardingUrl?.queryParams,
          description: 'Click to complete setup process',
          icon: 'settings_suggest'
        });
      }
      const tripInProcess = askInProgress.find((x) => x.inProgress === true);
      const onCorrectUrl =
        navEnd.url?.includes('dashboard') || navEnd.url?.includes('/trips?') || navEnd.url?.includes('/driverlogs?');
      if (tripInProcess && onCorrectUrl) {
        switch (tripInProcess.logType) {
          case DigitalLogType.Break:
          case DigitalLogType.TimeClock: {
            const title =
              tripInProcess.logType === DigitalLogType.Break
                ? 'On Break'
                : `Working: ${HourlyWorkTypeMapping[tripInProcess.hourlyWorkType]}`;
            const bannerClass = tripInProcess.logType === DigitalLogType.Break ? 'alert-warning' : undefined;
            return new BannerNotice({
              buttonText: 'Change Status',
              onClick: () => this.changeFlow(tripInProcess),
              description: title,
              icon: DefaultIcon.TimeClock.substring(3),
              bannerClass
            });
          }
          default:
            return new BannerNotice({
              buttonText: 'Go to Trip',
              routerLink: ['/driverlogs/wizard', tripInProcess.id],
              description: 'Click to complete the current trip',
              icon: 'settings_suggest'
            });
        }
      }
      return undefined;
    }),
    distinctUntilChanged((a, b) => this.bannerKey(a) === this.bannerKey(b))
  );
  private ux: UserExperience;
  private autoRefreshTimer: Subscription;

  //crud watcher
  crudSubscriber: Subscription;

  constructor(
    protected uxService: UserExperienceService,
    private crud: CrudService,
    private router: Router,
    private dialogService: DialogService
  ) {
    super();
    this.crud.notificationService = this;

    // Emit initial empty status to prevent blocking dependent components
    this.statusSyncBs.next(new StatusSync({}));

    //Refresh on UX Change - use setTimeout to prevent blocking app initialization
    this.uxService.currentUserExperience.pipe(distinctUntilChanged(), takeUntil(this.disposing$)).subscribe((newUx) => {
      this.ux = newUx;
      if (this.crudSubscriber) {
        this.crudSubscriber.unsubscribe();
        this.crudSubscriber = null;
      }
      // Defer refresh to next tick to avoid blocking component initialization
      setTimeout(() => this.refresh(), 0);
    });

    // Declarative banner injection via bannerNotice$
    this.bannerNotice$.pipe(takeUntil(this.disposing$)).subscribe((notice) => this.injectBanner(notice));
  }

  isClockedIn(): boolean {
    return !!this.statusSyncBs.value?.askInProgress?.find(
      (x) => x.inProgress === true && x.logType == DigitalLogType.TimeClock
    );
  }

  get isClockedIn$(): Observable<boolean> {
    return this.asksInProgress$.pipe(
      map((asks) => !!asks.find((x) => x.inProgress === true && x.logType == DigitalLogType.TimeClock)),
      distinctUntilChanged()
    );
  }

  clockIn(): void {
    this.loadClockInComponent().then((cmp) => {
      this.sDialog.showOrLink(
        cmp,
        {
          header: 'Clock In',
          data: {
            newEntry: true,
            autoSave: true,
            element: new Ask({
              logType: DigitalLogType.TimeClock,
              inProgress: true,
              employeeID: this.ux?.activeEmployeeID
            })
          },
          closable: true,
          modal: true
        },
        ['/driverlogs/clock-in'],
        { logType: DigitalLogType.TimeClock, inProgress: true },
        () => {
          this.refresh(true);
        }
      );
    });
  }

  changeFlow(tripInProcess?: Ask): void {
    tripInProcess ??= this.statusSyncBs.value?.askInProgress?.find(
      (x) => x.inProgress === true && (x.logType == DigitalLogType.TimeClock || x.logType == DigitalLogType.Break)
    );
    this.loadChangeFlowComponent().then((cmp) => {
      this.sDialog.showOrLink(
        cmp,
        {
          header: 'Change Status or Clock Out?',
          data: {
            newEntry: false,
            element: tripInProcess,
            autoSave: true
          }
        },
        ['/driverlogs/change-flow', tripInProcess?.id],
        {},
        () => {
          this.refresh(true);
        }
      );
    });
  }

  clockOut(tripInProcess?: Ask): void {
    tripInProcess ??= this.statusSyncBs.value?.askInProgress?.find(
      (x) => x.inProgress === true && (x.logType == DigitalLogType.TimeClock || x.logType == DigitalLogType.Break)
    );
    this.loadClockOutComponent().then((cmp) => {
      this.sDialog.showOrLink(
        cmp,
        {
          header: 'Clock Out',
          data: {
            newEntry: false,
            element: tripInProcess,
            autoSave: true
          }
        },
        ['/driverlogs/clock-out', tripInProcess?.id],
        {},
        () => {
          this.refresh(true);
        }
      );
    });
  }

  // Lazy component loaders with simple in-memory caching
  private clockInCmpRef?: Type<any>;
  private changeFlowCmpRef?: Type<any>;
  private clockOutCmpRef?: Type<any>;

  private loadClockInComponent(): Promise<Type<any>> {
    if (this.clockInCmpRef) return Promise.resolve(this.clockInCmpRef);
    return import('@app/modules/digital-logs/components/asks/ask-wizard-clock-in/ask-wizard-clock-in.component').then(
      (m) => (this.clockInCmpRef = m.AskWizardClockInComponent)
    );
  }
  private loadChangeFlowComponent(): Promise<Type<any>> {
    if (this.changeFlowCmpRef) return Promise.resolve(this.changeFlowCmpRef);
    return import('@app/modules/digital-logs/components/asks/ask-wizard-change-flow/ask-wizard-change-flow.component').then(
      (m) => (this.changeFlowCmpRef = m.AskWizardChangeFlowComponent)
    );
  }
  private loadClockOutComponent(): Promise<Type<any>> {
    if (this.clockOutCmpRef) return Promise.resolve(this.clockOutCmpRef);
    return import('@app/modules/digital-logs/components/asks/ask-wizard-clock-out/ask-wizard-clock-out.component').then(
      (m) => (this.clockOutCmpRef = m.AskWizardClockOutComponent)
    );
  }

  getAsksInProgress(): Ask[] | undefined {
    return this.statusSyncBs.value?.askInProgress?.map((ask) => new Ask(ask));
  }

  private lastRefresh: Dayjs;
  refresh(_force?: boolean): void {
    //has to be less then 3 since auto refresh is 3
    // if (!force && this.lastRefresh && SDate.newUtc().diff(this.lastRefresh, 'seconds') < 2) {
    //   console.log(
    //     `Too soon to refresh last refresh was ${this.lastRefresh?.format()} and now is ${SDate.newUtc().format()}`
    //   );
    //   return;
    // }
    if (this.ux?.activeEmployeeID) {
      if (this.crudSubscriber) {
        //console.log(`refreshing`);
        this.crud.refresh(this.schema); //need to test on actual refresh
      } else {
        //console.log(`creating new`);
        this.crudSubscriber = this.crud
          .get<StatusSync>(this.schema, null, true)
          .pipe(
            filter((result) => result.parameters.isLoading === false),
            takeUntil(this.disposing$)
          )
          .subscribe({
            next: (result) => {
              const newStatusSync = new StatusSync(result.data as Partial<StatusSync>);
              //console.log(`Status Sync: `, newStatusSync);
              this.statusSyncBs.next(newStatusSync);
              this.reschedule();
              this.lastRefresh = SDate.newUtc();
              //console.log(`refreshed as ${this.lastRefresh?.format()}`);
            },
            error: (error) => {
              console.warn('StatusSync failed to load, using empty status:', error);
              // Emit empty status to prevent indefinite blocking
              this.statusSyncBs.next(new StatusSync({}));
            }
          });
      }
    }
  }

  private reschedule(): void {
    if (this.autoRefreshTimer) {
      this.autoRefreshTimer.unsubscribe();
      this.autoRefreshTimer = null;
    }
    const cutOffTime = SDate.new().add(-1, 'hour');
    if (
      this.statusSyncBs.value.backEndJobs
        ?.filter((entry) => (SDate.isDayjs(entry.lastUpdate) ? entry.lastUpdate.isAfter(cutOffTime) : false))
        .map((entry) => entry.isInProgress)
        .reduce((a, b) => a || b, false)
    ) {
      //Take only one in case the Crud takes longer then 3 seconds and we get multiple refreshes
      this.autoRefreshTimer = timer(3 * 1000) // ???
        .pipe(take(1))
        .subscribe(() => this.refresh(true));
    }
  }

  show<T>(
    component: Type<unknown>,
    confirmData: DynamicDialogConfig<DialogEditConfig<T> | string | Observable<unknown>>,
    successCallback: (data: T) => void,
    errorCallback?: () => void
  ): void {
    this.sDialog.show(component, confirmData, successCallback, errorCallback);
  }

  showConfirm(confirmData: DynamicDialogConfig<SummitConfirmConfig>, accept: () => void, reject?: () => void): void {
    this.sDialog.confirmAction(confirmData, accept, reject);
  }

  showError(title: string, errorMessage: string, duration: number = 5000): void {
    this.triggerToast(`${title}\n${errorMessage}`, duration, 'error');
  }
  showWarn(title: string, errorMessage: string, duration: number = 5000): void {
    this.triggerToast(`${title}\n${errorMessage}`, duration, 'warn');
  }

  uxTriggerToast(message: string, duration: number, severity?: 'success' | 'warn' | 'info' | 'error'): void {
    this.triggerToast(message, duration, severity);
  }
  uxTriggerToastCustom(
    message: string,
    duration: number,
    actionText: '' | 'Go To' = '',
    stringURL?: string,
    severity?: 'success' | 'warn' | 'info' | 'error'
  ): void {
    return this.triggerToast(message, duration, severity, null, actionText, stringURL);
  }

  override showProgressSpinner(show: boolean, text?: string): void {
    //console.log(`showProgressSpinner: ${show} ${text}`);
    if (this.dialogService && show === true) {
      this.overlayDialogBs.next(text ?? 'Loading...');
      //only creates if it does not exist
      this.overlayDialogRef ??= this.dialogService.open(PageLoadingReactivePrimeComponent, {
        styleClass: 'loading-dialog',
        closable: false,
        style: { 'background-color': 'transparent', zIndex: 9999 },
        contentStyle: { 'background-color': 'transparent' },
        //dismissableMask: false,
        data: this.overlayDialogBs.asObservable(),
        showHeader: false,
        closeOnEscape: false,
        duplicate: true,
        modal: true
      });
    }
    if (this.overlayDialogRef && show === false) {
      this.overlayDialogRef.close();
      this.overlayDialogRef.destroy();
      this.overlayDialogRef = null;
    }
  }
}
